import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';
import { ExpeditionSheet } from '../components/ExpeditionSheet';
import { ParcelMap } from '../components/ParcelMap';
import { FenceDrawing } from '../components/FenceDrawing';

const COLORS: Record<string, string> = {
  grassland: '#C9B470', woodland: '#4A5A2E', scrub: '#A8894E', wetland: '#6FA8A0', rocky: '#7A6F66',
};

export function MapScreen() {
  const { sim, bridge, panel, openPanel,setTab } = useStore();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [view,setView]=useState<'owned'|'market'>('owned');
  const [building,setBuilding]=useState(false);
  const [enclosureId,setEnclosureId]=useState<string|null>(null);
  const [confirmSell, setConfirmSell] = useState<string | null>(null);

  const owned = sim.parcels.filter((p) => p.owned);
  const ownedHa = owned.reduce((a, p) => a + p.hectares, 0);
  const byClass = new Map<string, number>();
  for (const p of owned) byClass.set(p.landClass, (byClass.get(p.landClass) ?? 0) + p.hectares);
  const easements = sim.business?.taxes?.easements ?? [];
  const detail = sim.parcels.find((p) => p.id === detailId) ?? (view==='owned'?owned.at(0)??null:null);
  const detailEnc = detail ? sim.enclosures.filter((e) => e.parcelId === detail.id) : [];
  const detailTraps = detail ? sim.traps.filter((t) => t.parcelId === detail.id) : [];

  return (
    <>
      <div class="screen-scroll">
        <div class="eyebrow eyebrow-rule">Land and enclosure map</div>
        <div class="card row-label" style="padding:10px 12px;white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px;background:var(--ui-parchment)">View saved fences and pens on your owned land, open an animal, or enter fence drawing. Global terrain and parcel adjacency are not mapped yet.</div>
        <div style="display:flex;gap:8px;margin-top:8px"><button class={view==='owned'?'btn btn-primary':'btn btn-quiet'} style="flex:1;background:var(--ui-parchment);color:var(--ui-ink)" onClick={()=>{setView('owned');setDetailId(null);setBuilding(false);}}>Owned land</button><button class={view==='market'?'btn btn-primary':'btn btn-quiet'} style="flex:1;background:var(--ui-parchment);color:var(--ui-ink)" onClick={()=>{setView('market');setDetailId(null);setBuilding(false);}}>Land market</button></div>

        {owned.length > 0 && (
          <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:6px;padding:8px 12px">
            <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">
              {owned.length} parcel{owned.length === 1 ? '' : 's'} owned - {ownedHa} ha
              {byClass.size > 0 && ` (${[...byClass.entries()].map(([c, ha]) => `${c.toLowerCase()} ${ha}`).join(' / ')})`}
            </div>
          </div>
        )}

        <div class="card" style="padding:12px;margin-top:8px">
          {view==='owned'&&owned.length===0&&<div class="row-label">No owned land. Open Land market to buy your first parcel.</div>}
          {view==='owned'&&owned.length>0&&<label class="row-label">Owned parcel<select aria-label="Owned parcel" style="width:100%;padding:8px;margin-top:6px" value={detail?.id??''} onChange={e=>{setDetailId(e.currentTarget.value);setBuilding(false);setEnclosureId(null);}}>{owned.map(p=><option key={p.id} value={p.id}>{p.id} - {p.hectares} ha {p.landClass}</option>)}</select></label>}
          {view==='market'&&<>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">
            {sim.parcels.map((p) => {
              const col = COLORS[p.landClass.toLowerCase()] ?? '#8A6A3C';
              const hasEasement = easements.includes(p.id);
              return (
                <button
                  key={p.id}
                  class="card"
                  style={`display:flex;flex-direction:column;gap:4px;padding:10px 6px;align-items:center;background:${col}33;cursor:pointer;${p.owned ? '' : 'opacity:0.9;'}${detailId === p.id ? 'outline:2px solid var(--ui-amber, #b97a2a);' : ''}`}
                  onClick={() => { setDetailId(detailId === p.id ? null : p.id); setConfirmSell(null); }}
                >
                  <span style={`width:100%;height:34px;border-radius:calc(var(--ui-radius) - 6px);background:${col};border:var(--ui-border)`} />
                  <span class="row-label">{p.id} - {p.landClass}</span>
                  <span style="font-size:11px;font-weight:600">{p.hectares} ha{p.owned ? ' - owned' : ` - $${fmtMoney(p.price)}`}</span>
                  {hasEasement && <span class="row-label" style="font-size:10px">easement</span>}
                </button>
              );
            })}
            {sim.parcels.length === 0 && <p style="grid-column:1/-1;font-size:13px;color:var(--ui-ink-soft)">The parcel market opens once the scenario starts.</p>}
          </div>
          </>}
        </div>

        {detail && (
          <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
            <div class="row-value" style="text-transform:capitalize">{detail.id} - {detail.landClass.toLowerCase()}</div>
            <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal;margin-top:4px">
              {detail.hectares} ha - {detail.distanceKm} km from the gate
              {easements.includes(detail.id) && ' - conservation easement: building permanently blocked'}
            </div>
            {detailEnc.length > 0 && (
              <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal;margin-top:4px">
                Enclosures: {detailEnc.map((e) => `${e.type.replace(/_/g, ' ')} (${e.animalCount} animal${e.animalCount === 1 ? '' : 's'})`).join(', ')}
              </div>
            )}
            {detailTraps.length > 0 && (
              <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal;margin-top:4px">
                Traps: {detailTraps.map((t) => t.type.replace(/_/g, ' ')).join(', ')}
              </div>
            )}
            {detail.owned && detailEnc.length === 0 && detailTraps.length === 0 && (
              <div class="row-label" style="text-transform:none;letter-spacing:0.01em;margin-top:4px">Undeveloped{easements.includes(detail.id) ? ' - easement land stays wild' : ' - build from the Park screen'}.</div>
            )}
            <div style="display:flex;gap:8px;margin-top:8px">
              {!detail.owned && (
                <button class="btn btn-quiet" style="flex:1" onClick={() => { bridge.send('parcel.buy', { parcelId: detail.id }); }}>Buy for ${fmtMoney(detail.price)}</button>
              )}
              {detail.owned && detailEnc.length === 0 && confirmSell !== detail.id && (
                <button class="btn btn-quiet" style="flex:1" onClick={() => { setConfirmSell(detail.id); }}>Sell parcel</button>
              )}
              {detail.owned && detailEnc.length === 0 && confirmSell === detail.id && (
                <>
                  <button class="btn btn-primary" style="flex:1" onClick={() => { bridge.send('parcel.sell', { parcelId: detail.id }); setConfirmSell(null); setDetailId(null); }}>Confirm sale at current land value</button>
                  <button class="btn btn-quiet" onClick={() => { setConfirmSell(null); }}>Keep it</button>
                </>
              )}
            </div>
            {detail.owned && detailEnc.length > 0 && (
              <div class="row-label" style="margin-top:6px;text-transform:none;letter-spacing:0.01em;white-space:normal">
                The sim turns down the sale while enclosures stand on this parcel.
              </div>
            )}
            {detail.owned && (() => {
              const layout = sim.fenceLayouts[detail.id] as import('../bridge').UiFenceLayout | undefined;
              return layout ? <>
                <ParcelMap layout={layout} enclosures={detailEnc} penCells={sim.penCells} onSelect={id=>{setEnclosureId(id);}} />
                {enclosureId&&detailEnc.some(e=>e.id===enclosureId)&&<div class="card" style="padding:8px;margin-top:6px"><div class="row-value">Animals in this enclosure</div>{sim.animals.filter(a=>a.enclosure===enclosureId).length===0&&<div class="row-label">No animals housed here.</div>}{sim.animals.filter(a=>a.enclosure===enclosureId).map(a=><button key={a.id} class="btn btn-quiet" style="display:block;width:100%;margin-top:6px" onClick={()=>{setTab('herd');openPanel({kind:'animal',id:a.id});}}>{a.name} - open animal care</button>)}</div>}
                <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={()=>{setBuilding(!building);}}>{building?'Close fence drawing':'Open fence drawing'}</button>
                {building&&<FenceDrawing parcelId={detail.id} layout={layout} />}
              </> : <div class="row-label">Parcel grid is not available.</div>;
            })()}
          </div>
        )}

        <button class="btn btn-primary" style="margin-top:8px" onClick={() => { openPanel({ kind: 'expedition' }); }}>Plan expedition</button>
      </div>
      {panel?.kind === 'expedition' && <ExpeditionSheet />}
    </>
  );
}
